/**
 * The idea: the client's own concept comes first. Twists only when they ask: their concept told in the shape of a
 * format travelling on TikTok in their market right now (Breakout formats), each with the post that travelled to
 * watch. With no breakout scan for the market, Lykke offers ideas from the Brand OS instead.
 */
import { useState } from 'react';
import { Check, Loader2, Play, RefreshCw, Sparkles } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { LazyTikTokEmbed } from '@/components/tiktok/LazyTikTokEmbed';
import { tiktokEmbedV2Url } from '@/components/tiktok/embedUrl';
import type { OrderIdea } from '../types';
import type { Twist, TwistsResult } from './types';
import { chosenIdea, PRESET_FOR, short } from './state';
import { sceneFor } from './parts';
import { FormatStrip } from './Visuals';
import { Card, H2, TextCommit } from './ui';
import type { StepProps } from './StepsStart';

const EVIDENCE: Record<string, [string, string]> = { proven: ['Proven pattern', 'bg-[#00b1a5]/10 text-[#0e9384]'], emerging: ['Emerging', 'bg-[#fffaeb] text-[#b54708]'], 'one-off': ['Early signal', 'bg-[#f2f4f7] text-[#667085]'], default: ['Early signal', 'bg-[#f2f4f7] text-[#667085]'] };

export function IdeaSection({ prep, s, set }: StepProps) {
  const [res, setRes] = useState<TwistsResult | null>(null);
  const [ideas, setIdeas] = useState<OrderIdea[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const market = prep.markets.find((m) => m.id === s.market)?.name || s.market;
  const hasOwn = !!s.concept.title.trim();
  const usingOwn = hasOwn && !s.idea;
  const idea = chosenIdea(s);

  const ask = async (more = false) => {
    setBusy(true); setError(null);
    try {
      const r = await coworkApi.campaignOrder.v5.twists({ brandId: prep.brand.id, market: s.market, product: s.productName || undefined, outcome: s.outcome || undefined, concept: hasOwn ? s.concept : undefined, more });
      setRes(r);
      if (!r.radar) {
        // No breakout scan for this market yet: ideas from the Brand OS, for the outcome's playbook.
        const goal = prep.goals.find((g) => g.id === PRESET_FOR[s.outcome || ''])?.id || prep.goals.find((g) => g.objective === s.outcome)?.id || prep.goals[0]?.id;
        const audiences = [...prep.audiences.filter((a) => s.audienceIds.includes(a.id)).map((a) => a.name), ...(s.audienceText.trim() ? [s.audienceText.trim()] : [])];
        if (goal) setIdeas((await coworkApi.campaignOrder.ideas({ brandId: prep.brand.id, goal, market: s.market, productName: s.productName || undefined, audiences, more })).ideas);
      }
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const setConcept = (patch: Partial<V5Concept>, field: string, value: string) => set({ concept: { ...s.concept, ...patch } }, value ? { field, value: value.slice(0, 80) } : undefined);
  const pickTwist = (t: Twist) => set({ idea: { title: t.title, hook: t.hook, description: t.description, keyMessage: s.concept.keyMessage || undefined, source: 'twist', format: t.formatName, exampleUrl: t.example?.url } }, { field: 'Idea', value: `${t.title} (twist: ${t.formatName})` });
  const pickIdea = (i: OrderIdea) => set({ idea: { title: i.title, hook: i.hook, description: i.description, keyMessage: i.keyMessage, source: 'lykke' } }, { field: 'Idea', value: i.title });
  const twists = res?.twists || [];

  return (
    <section className="flex flex-col gap-3">
      <H2 note="Yours first. Twists if you want them.">The idea</H2>

      <Card className={cn('flex flex-col gap-3 transition-all', usingOwn && 'border-[#be43ff] bg-[#be43ff]/[0.02]')}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>Your concept</span>
            {usingOwn && <span className="inline-flex items-center gap-1 rounded-lg bg-[#be43ff]/8 px-2 py-0.5 text-[11px] text-[#be43ff]" style={{ fontWeight: 600 }}><Check className="h-3 w-3" aria-hidden />In your order</span>}
          </div>
          {s.idea && hasOwn && <button type="button" onClick={() => set({ idea: null }, { field: 'Idea', value: s.concept.title })} className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-[12px] text-[#344054] shadow-sm hover:bg-gray-50" style={{ fontWeight: 600 }}>Use my concept instead</button>}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <TextCommit id="concept-title" label="Name it" value={s.concept.title} placeholder="Winter test" onCommit={(v) => setConcept({ title: v }, 'Your concept', v)} />
          <TextCommit id="concept-key" label="Key message (optional)" value={s.concept.keyMessage} placeholder="Made for the cold" onCommit={(v) => setConcept({ keyMessage: v }, 'Key message', v)} />
        </div>
        <TextCommit id="concept-what" area label="What happens" value={s.concept.description} placeholder="What creators do and show, in a sentence or two" onCommit={(v) => setConcept({ description: v }, 'What happens', v)} />
      </Card>

      <Card tone="soft" className="flex flex-wrap items-center gap-3">
        <span className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-white text-[#be43ff] shadow-sm"><Sparkles className="h-4 w-4" aria-hidden /></span>
        <p className="min-w-0 flex-1 text-[13px] text-[#344054]">
          <b className="text-[#101828]">{hasOwn ? 'Want a twist?' : 'No concept yet?'}</b>{' '}
          Lykke can tell {hasOwn ? 'your concept' : `your ${s.productName || 'product'}`} in the shape of formats travelling on TikTok in {market} right now, with the video that travelled.
        </p>
        <button type="button" onClick={() => void ask(!!res)} disabled={busy} className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-[#be43ff]/30 bg-white px-4 text-[13px] text-[#be43ff] transition-colors hover:bg-[#be43ff]/5 disabled:opacity-60" style={{ fontWeight: 600 }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : res ? <RefreshCw className="h-4 w-4" aria-hidden /> : <Sparkles className="h-4 w-4" aria-hidden />}
          {res ? 'New twists' : hasOwn ? 'Show twists' : 'Show ideas'}
        </button>
      </Card>
      {error && <p className="text-[12.5px] text-[#b42318]">{error}</p>}

      {twists.length > 0 && (
        <div className="grid gap-3 lg:grid-cols-3">
          {twists.map((t) => <TwistCard key={t.id} t={t} on={s.idea?.source === 'twist' && s.idea.title === t.title} onUse={() => pickTwist(t)} />)}
        </div>
      )}
      {res && !res.radar && (
        <>
          <p className="text-[12.5px] text-[#667085]">No breakout scan for {market} yet, so these come from your Brand OS.</p>
          <div className="grid gap-3 lg:grid-cols-3">
            {(ideas || []).map((i, k) => {
              const on = s.idea?.source === 'lykke' && s.idea.title === i.title;
              return (
                <div key={i.id || i.title} className={cn('flex flex-col gap-2 rounded-2xl border bg-white p-4 shadow-sm transition-all', on ? 'border-[#be43ff] bg-[#be43ff]/[0.04]' : 'border-gray-100')}>
                  <div className="h-20 rounded-xl" style={{ background: sceneFor(k) }} aria-hidden />
                  <div className="text-[15px] text-[#101828]" style={{ fontWeight: 600 }}>{i.title}</div>
                  {i.hook && <div className="text-[13px] text-[#be43ff]" style={{ fontWeight: 600 }}>“{i.hook}”</div>}
                  <p className="text-[12.5px] leading-relaxed text-[#667085]">{i.description}</p>
                  <UseButton on={on} onClick={() => pickIdea(i)} label="Use this idea" />
                </div>
              );
            })}
          </div>
        </>
      )}

      {idea && (
        <Card>
          <div className="mb-3 text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>{idea.title} in every part</div>
          <FormatStrip parts={s.parts} ownFilm={s.creative === 'own'} idea={{ title: idea.title, hook: idea.hook, caption: idea.hook || idea.title, scene: sceneFor(0), who: `@${(prep.brand.name || 'creator').toLowerCase().replace(/[^a-z0-9]/g, '')}` }} />
        </Card>
      )}
    </section>
  );
}

type V5Concept = StepProps['s']['concept'];

const UseButton = ({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) => (
  <button type="button" onClick={onClick} aria-pressed={on} className={cn('mt-auto inline-flex h-9 w-fit items-center gap-1.5 rounded-xl px-4 text-[13px] transition-all', on ? 'bg-[#be43ff]/8 text-[#be43ff]' : 'bg-[#be43ff] text-white hover:bg-[#a830e0] active:scale-[0.97]')} style={{ fontWeight: 600 }}>
    {on ? <><Check className="h-4 w-4" aria-hidden />In your order</> : label}
  </button>
);

/** A twist with the post that travelled: a still with the hook line until they press play, then TikTok's own player. */
function TwistCard({ t, on, onUse }: { t: Twist; on: boolean; onUse: () => void }) {
  const [watch, setWatch] = useState(false);
  const [label, cls] = EVIDENCE[t.evidence] || EVIDENCE.default;
  const ex = t.example;
  return (
    <div className={cn('flex flex-col gap-2.5 rounded-2xl border bg-white p-3 shadow-sm transition-all', on ? 'border-[#be43ff] bg-[#be43ff]/[0.04]' : 'border-gray-100')}>
      {ex?.videoId && (watch
        ? <LazyTikTokEmbed src={tiktokEmbedV2Url(ex.videoId)} title={`Example: @${ex.handle}`} width={300} height={533} className="mx-auto w-full max-w-[300px] overflow-hidden rounded-xl" style={{ height: 533 }} />
        : (
          <button type="button" onClick={() => setWatch(true)} className="group relative flex h-44 flex-col justify-end overflow-hidden rounded-xl bg-[#2e024c] p-3 text-left text-white" aria-label={`Watch the example by @${ex.handle}`}>
            <span className="absolute inset-0 opacity-60" style={{ background: 'radial-gradient(circle at 75% 25%, #be43ff 0, transparent 55%)' }} aria-hidden />
            <span className="absolute left-1/2 top-1/2 grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-[#2e024c] transition-transform group-hover:scale-105"><Play className="ml-0.5 h-5 w-5" aria-hidden /></span>
            <span className="relative text-[10px] uppercase tracking-[0.08em] text-white/70" style={{ fontWeight: 600 }}>Example that travelled</span>
            {ex.hookLine && <span className="relative mt-0.5 line-clamp-2 text-[13px]" style={{ fontWeight: 600 }}>“{ex.hookLine}”</span>}
            <span className="relative mt-1 text-[11px] text-white/80">@{ex.handle} · {short(ex.views)} views{typeof ex.multiple === 'number' && ex.multiple > 1 ? ` · ${Math.round(ex.multiple)}× their usual` : ''}</span>
          </button>
        ))}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="rounded-lg bg-[#be43ff]/8 px-2 py-0.5 text-[11px] text-[#be43ff]" style={{ fontWeight: 600 }}>{t.formatName}</span>
        <span className={cn('rounded-lg px-2 py-0.5 text-[11px]', cls)} style={{ fontWeight: 600 }}>{label}</span>
      </div>
      <div className="text-[15px] text-[#101828]" style={{ fontWeight: 600 }}>{t.title}</div>
      {t.hook && <div className="text-[13px] text-[#be43ff]" style={{ fontWeight: 600 }}>“{t.hook}”</div>}
      <p className="text-[12.5px] leading-relaxed text-[#667085]">{t.description}</p>
      {t.keeps && <p className="text-[12px] text-[#344054]"><b>Keeps from yours:</b> {t.keeps}</p>}
      <p className="text-[11.5px] text-[#98a2b3]">{t.creators} creators, {t.posts} posts in this format. Worth testing, not a promise.</p>
      <UseButton on={on} onClick={onUse} label="Use this twist" />
    </div>
  );
}
